import React, { useState } from 'react';
import './DecisionCalculator.css';

const ANSWER_KEYS = ['visaType', 'documentsComplete', 'applicationDate', 'previousRefusals'];

const DecisionCalculator = ({ onUpgrade, locked = false, hasPremiumAccess = false }) => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({
    visaType: '',
    documentsComplete: '',
    applicationDate: '',
    previousRefusals: ''
  });
  const [result, setResult] = useState(null);

  const questions = {
    1: {
      question: 'What type of visa did you apply for?',
      options: [
        { value: 'study', label: 'Study Visa', score: 88 },
        { value: 'work', label: 'Work Permit / Critical Skills', score: 92 },
        { value: 'tourist', label: 'Tourist Visa', score: 85 },
        { value: 'family', label: 'Family Reunification', score: 90 }
      ]
    },
    2: {
      question: 'Are all your supporting documents complete and accurate?',
      options: [
        { value: 'yes-verified', label: 'Yes, all verified and complete', score: 15 },
        { value: 'yes', label: 'Yes, I believe so', score: 5 },
        { value: 'mostly', label: 'Mostly, with minor gaps', score: -10 },
        { value: 'unsure', label: 'Unsure', score: -20 }
      ]
    },
    3: {
      question: 'When did you submit your application?',
      options: [
        { value: 'recent', label: 'Within last 4 weeks', score: 0 },
        { value: 'medium', label: '1-2 months ago', score: 5 },
        { value: 'long', label: '2-3 months ago', score: 10 },
        { value: 'very-long', label: 'Over 3 months ago', score: 15 }
      ]
    },
    4: {
      question: 'Have you had any previous visa refusals?',
      options: [
        { value: 'none', label: 'No previous refusals', score: 10 },
        { value: 'one', label: 'One previous refusal (resolved)', score: -5 },
        { value: 'multiple', label: 'Multiple refusals', score: -15 },
        { value: 'recent', label: 'Recent refusal (within 6 months)', score: -25 }
      ]
    }
  };

  const triggerUpgrade = () => {
    if (typeof onUpgrade === 'function') onUpgrade();
  };

  const handleAnswer = (questionId, option) => {
    if (locked) {
      triggerUpgrade();
      return;
    }
    const updatedAnswers = { ...answers };
    const questionKey = ANSWER_KEYS[questionId - 1];
    updatedAnswers[questionKey] = option;
    setAnswers(updatedAnswers);

    if (questionId < 4) {
      setStep(questionId + 1);
    } else {
      calculateResult(updatedAnswers);
    }
  };

  const calculateResult = (finalAnswers) => {
    let profileScore = questions[1].options.find(o => o.value === finalAnswers.visaType)?.score || 85;
    
    ANSWER_KEYS.forEach((key, index) => {
      if (index > 0 && finalAnswers[key]) {
        const questionNum = index + 1;
        const option = questions[questionNum].options.find(o => o.value === finalAnswers[key]);
        if (option) {
          profileScore += option.score;
        }
      }
    });

    const finalScore = Math.min(98, Math.max(45, profileScore));
    
    let status = 'Moderate';
    let statusColor = 'status-moderate';
    let statusIcon = '🟡';
    let advice = 'Your application looks reasonable, but ensure all documents are perfect.';
    
    if (finalScore >= 85) {
      status = 'Strong';
      statusColor = 'status-strong';
      statusIcon = '✅';
      advice = 'Your application profile is strong! Stay patient during the processing time.';
    } else if (finalScore >= 70) {
      status = 'Good';
      statusColor = 'status-good';
      statusIcon = '👍';
      advice = 'Strong checklist signal. Keep your documents consistent and track your application closely.';
    } else if (finalScore < 60) {
      status = 'Needs Attention';
      statusColor = 'status-low';
      statusIcon = '⚠️';
      advice = 'Consider consulting an immigration advisor to strengthen your application.';
    }

    setResult({
      score: finalScore,
      status,
      statusColor,
      statusIcon,
      advice,
    });
  };

  const resetCalculator = () => {
    if (locked) {
      triggerUpgrade();
      return;
    }
    setStep(1);
    setAnswers({
      visaType: '',
      documentsComplete: '',
      applicationDate: '',
      previousRefusals: ''
    });
    setResult(null);
  };

  if (result) {
    return (
      <div className="decision-calculator">
        <div className="calculator-header">
          <h2>📊 Your Profile Readiness</h2>
          <button onClick={resetCalculator} className="reset-calculator-btn">
            🔄 Recalculate
          </button>
        </div>

        <div className="result-section">
          <div className={`probability-circle ${result.statusColor}`}>
            <div className="probability-value">{result.score}/100</div>
            <div className="probability-label">{result.status}</div>
          </div>

          <div className="result-details">
            <div className="result-icon">{result.statusIcon}</div>
            <div className="result-text">
              <h3>Your profile readiness signal</h3>
              <p>{result.advice} This is an educational checklist score, not a visa approval probability or immigration decision.</p>
            </div>
          </div>

          <div className="result-stats">
            <div className="result-stat">
              <div className="result-stat-label">What this measures</div>
              <div className="result-stat-value">Document and profile readiness</div>
            </div>
          </div>

          <div className="critical-insight">
            <div className="insight-icon">💡</div>
            <div className="insight-content">
              <strong>Planning reminder:</strong> Processing times vary by visa type, office, season, and individual circumstances.
              <p>Use official immigration guidance and your application office for current information.</p>
            </div>
          </div>

          <div className="calculator-cta">
            <div className="cta-message">
              <h4>Monitor Your Official Decision</h4>
              <p>Get email alerts, optional browser notifications and all 13 premium planning tools</p>
            </div>
            {!hasPremiumAccess && (
              <button className="btn-primary btn-shimmer calculator-cta-btn" onClick={triggerUpgrade}>
                Unlock Alerts + 13 Tools for ₹349 →
              </button>
            )}
            <p className="cta-footnote">
              Official-decision monitoring + 13 premium planning tools
            </p>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[step];

  return (
    <div className="decision-calculator">
      <div className="calculator-header">
        <h2>🎯 How Ready Is My Application?</h2>
        <p className="calculator-subtitle">Answer 4 quick questions to get an educational profile-readiness assessment</p>
      </div>

      <div className="progress-indicator">
        <div className="progress-steps">
          {[1, 2, 3, 4].map(num => (
            <div key={num} className={`progress-step ${step >= num ? 'active' : ''} ${step > num ? 'completed' : ''}`}>
              <div className="step-circle">{step > num ? '✓' : num}</div>
            </div>
          ))}
        </div>
        <div className="progress-bar-container">
          <div className="decision-progress-bar" style={{ width: `${(step / 4) * 100}%` }}></div>
        </div>
      </div>

      <div className="question-section">
        <h3 className="decision-question-text">{currentQuestion.question}</h3>
        <div className="options-grid">
          {currentQuestion.options.map((option) => (
            <button
              key={option.value}
              className="option-button"
              onClick={() => handleAnswer(step, option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {step > 1 && (
        <button className="back-button" onClick={() => {
          if (locked) {
            triggerUpgrade();
            return;
          }
          setStep(step - 1);
        }}>
          ← Back
        </button>
      )}
    </div>
  );
};

export default DecisionCalculator;
